<!--app\javascript\dashboard\routes\dashboard\bulkmessage\components\MessagePreview\WhatsAppPreview.vue-->

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { splitName } from '@chatwoot/utils';
import whatsappLightBg from '../../assets/whatsappLight.jpg';
import whatsappDarkBg from '../../assets/whatsappDark.jpg';
import {
  formatFlagDisplayText,
  replaceFlagEmojisWithIcons,
} from 'shared/helpers/flagEmoji';

const props = defineProps({
  message: {
    type: String,
    default: '',
  },
  recipientName: {
    type: String,
    default: '',
  },
  attachments: {
    type: Array,
    default: () => [],
  },
  parameters: {
    type: Array,
    default: () => [],
  },
  buttons: {
    type: Array,
    default: () => [],
  },
  recipients: {
    type: Array,
    default: () => [],
  },
  additionalColumns: {
    type: Array,
    default: () => [],
  },
});

const currentTime = computed(() => {
  return new Date().toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
});

// Reactive dark mode detection
const isDarkMode = ref(false);

// MutationObserver to watch for class changes on document.body
let observer;

const updateDarkMode = () => {
  isDarkMode.value = document.body.classList.contains('dark');
};

onMounted(() => {
  // Set initial value
  updateDarkMode();

  // Watch for changes to document.body class
  observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'class'
      ) {
        updateDarkMode();
      }
    });
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
  }
});

const whatsappChatStyle = computed(() => {
  const backgroundImage = isDarkMode.value ? whatsappDarkBg : whatsappLightBg;
  return {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  };
});

const recipientNameHtml = computed(() =>
  formatFlagDisplayText(props.recipientName || 'Nome do Cliente')
);

// Helper functions for file types
const isImageFile = type => {
  return type && type.startsWith('image/');
};

const isVideoFile = type => {
  return type && type.startsWith('video/');
};

const isAudioFile = type => {
  return type && type.startsWith('audio/');
};

const formatFileSize = sizeInBytes => {
  if (!sizeInBytes) return '0 B';
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }
  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  }
  return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Check if message has attachments
const hasAttachments = computed(() => {
  return props.attachments && props.attachments.length > 0;
});

// Helper function to process custom variables with real or sample data (for template parameter preview)
const processCustomVariables = (text, recipient = null) => {
  if (!text) return text;

  let processed = text;

  if (recipient) {
    // Use real recipient data when available
    if (recipient.name) {
      const { firstName, lastName } = splitName(recipient.name);
      const lastNameOrFirstName = lastName || firstName;

      processed = processed.replace(/\{\{nome\}\}/g, firstName);
      processed = processed.replace(/\{\{first_name\}\}/g, firstName);
      processed = processed.replace(/\{\{sobrenome\}\}/g, lastNameOrFirstName);
      processed = processed.replace(/\{\{last_name\}\}/g, lastNameOrFirstName);
      processed = processed.replace(/\{\{nome_completo\}\}/g, recipient.name);
      processed = processed.replace(/\{\{name\}\}/g, recipient.name);
    }

    if (recipient.phone) {
      processed = processed.replace(/\{\{telefone\}\}/g, recipient.phone);
      processed = processed.replace(/\{\{phone\}\}/g, recipient.phone);
    }

    if (recipient.email) {
      processed = processed.replace(/\{\{email\}\}/g, recipient.email);
    }

    // Replace custom field variables
    if (recipient.customFields) {
      Object.keys(recipient.customFields).forEach(field => {
        const upperField = field.toUpperCase();
        const regex = new RegExp(`\\{\\{${upperField}\\}\\}`, 'g');
        processed = processed.replace(regex, recipient.customFields[field]);
      });
    }
  } else {
    // Fall back to sample values when no recipient data available
    processed = processed.replace(/\{\{nome\}\}/g, 'João');
    processed = processed.replace(/\{\{first_name\}\}/g, 'João');
    processed = processed.replace(/\{\{sobrenome\}\}/g, 'Silva');
    processed = processed.replace(/\{\{last_name\}\}/g, 'Silva');
    processed = processed.replace(/\{\{nome_completo\}\}/g, 'João Silva');
    processed = processed.replace(/\{\{name\}\}/g, 'João Silva');
    processed = processed.replace(/\{\{telefone\}\}/g, '+55 11 99999-9999');
    processed = processed.replace(/\{\{phone\}\}/g, '+55 11 99999-9999');
    processed = processed.replace(/\{\{email\}\}/g, 'exemplo@email.com');
  }

  return processed;
};

// Format WhatsApp text with proper formatting and variable replacement
const formatWhatsAppText = text => {
  if (!text) return '';

  let formatted = text;

  // First, replace bulk message variables with actual recipient data
  if (props.recipients && props.recipients.length > 0) {
    const firstRecipient = props.recipients[0];

    // Replace basic variables
    if (firstRecipient.name) {
      // Use splitName utility to properly split first and last names
      const { firstName, lastName } = splitName(firstRecipient.name);

      // If no last name, fallback to first name
      const lastNameOrFirstName = lastName || firstName;

      // Replace first name variables
      formatted = formatted.replace(/\{\{nome\}\}/g, firstName);
      formatted = formatted.replace(/\{\{first_name\}\}/g, firstName);

      // Replace last name variables (fallback to first name if no last name)
      formatted = formatted.replace(/\{\{sobrenome\}\}/g, lastNameOrFirstName);
      formatted = formatted.replace(/\{\{last_name\}\}/g, lastNameOrFirstName);

      // Replace full name variables
      formatted = formatted.replace(/\{\{nome_completo\}\}/g, firstRecipient.name);
      formatted = formatted.replace(/\{\{name\}\}/g, firstRecipient.name);
    }

    // Replace phone
    if (firstRecipient.phone) {
      formatted = formatted.replace(/\{\{telefone\}\}/g, firstRecipient.phone);
      formatted = formatted.replace(/\{\{phone\}\}/g, firstRecipient.phone);
    }

    // Replace email
    if (firstRecipient.email) {
      formatted = formatted.replace(/\{\{email\}\}/g, firstRecipient.email);
    }

    // Replace custom field variables
    const allCustomFields = new Set();
    
    // Add from additionalColumns
    if (props.additionalColumns && props.additionalColumns.length > 0) {
      props.additionalColumns.forEach(column => allCustomFields.add(column));
    }
    
    // Add from recipient customFields (excluding DDI)
    if (firstRecipient.customFields) {
      Object.keys(firstRecipient.customFields).forEach(field => {
        const lowerField = field.toLowerCase();
        if (!lowerField.includes('ddi') && !lowerField.includes('country')) {
          allCustomFields.add(field);
        }
      });
    }
    
    // Replace all custom field variables
    allCustomFields.forEach(column => {
      const upperColumn = column.toUpperCase();
      const regex = new RegExp(`\\{\\{${upperColumn}\\}\\}`, 'g');
      const value = firstRecipient[column] || firstRecipient.customFields?.[column] || `[${column}]`;
      formatted = formatted.replace(regex, value);
    });
  }

  // Replace template parameters {{1}}, {{2}}, etc. with actual values (for WhatsApp templates)
  if (props.parameters && props.parameters.length > 0) {
    // Get first recipient for variable replacement in parameters
    const firstRecipient = props.recipients && props.recipients.length > 0 ? props.recipients[0] : null;

    props.parameters.forEach(param => {
      const placeholder = `{{${param.index}}}`;
      // Process custom variables in parameter value before displaying (use real recipient data if available)
      const rawValue = param.value || param.example || `{${param.index}}`;
      const value = processCustomVariables(rawValue, firstRecipient);
      formatted = formatted.replace(
        new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'),
        value
      );
    });
  }

  // Process WhatsApp template footer (before other formatting)
  // Replace __FOOTER__text__FOOTER_END__ with proper HTML and class
  formatted = formatted.replace(
    /__FOOTER__([\s\S]*?)__FOOTER_END__/g,
    '<span class="whatsapp-footer">$1</span>'
  );

  // WhatsApp Bold formatting: Use placeholders to prevent double-processing
  // Only **text** and ***text*** should be bold, *text* should remain plain text
  
  // Handle 3+ asterisks first: ***text*** becomes <strong>*text*</strong>
  formatted = formatted.replace(/\*{3,}([^*\s](?:[^*]*[^*\s])?)\*{3,}/g, (match, content) => {
    const startAsterisks = match.match(/^\*+/)[0];
    const endAsterisks = match.match(/\*+$/)[0];
    
    // Use 2 asterisks for formatting, include the rest in content
    const extraStart = startAsterisks.slice(2);
    const extraEnd = endAsterisks.slice(2);
    return `__BOLD_START__${extraStart}${content}${extraEnd}__BOLD_END__`;
  });

  // Handle exactly 2 asterisks: **text**
  formatted = formatted.replace(
    /\*{2}([^*\s](?:[^*]*[^*\s])?)\*{2}/g,
    '__BOLD_START__$1__BOLD_END__'
  );

  // Convert placeholders to actual HTML tags
  formatted = formatted.replace(/__BOLD_START__/g, '<strong>');
  formatted = formatted.replace(/__BOLD_END__/g, '</strong>');

  // Italic: _text_ (no spaces adjacent to underscores)
  formatted = formatted.replace(
    /_([^_\s](?:[^_]*[^_\s])?)_/g,
    '<em>$1</em>'
  );

  // Strikethrough: ~text~ (no spaces adjacent to tildes)
  formatted = formatted.replace(
    /~([^~\s](?:[^~]*[^~\s])?)~/g, 
    '<del>$1</del>'
  );

  // Code block: ```text``` (three backticks - matches WhatsAppEditor)
  formatted = formatted.replace(
    /```([^`]+)```/g,
    '<code class="bg-n-slate-2 dark:bg-n-slate-8 px-2 py-1 rounded text-xs font-mono">$1</code>'
  );

  // Single backtick code: `text` (matches WhatsAppEditor code format)
  formatted = formatted.replace(
    /`([^`]+)`/g,
    '<code class="bg-n-slate-2 dark:bg-n-slate-8 px-1 rounded text-xs font-mono">$1</code>'
  );

  return replaceFlagEmojisWithIcons(formatted);
};
</script>

<template>
  <div class="whatsapp-container">
    <div class="whatsapp-device">
      <!-- Status bar -->
      <div class="whatsapp-status-bar">
        <div class="flex justify-between items-center px-6">
          <span class="text-xs">{{ currentTime }}</span>
          <div class="flex items-center gap-1">
            <i class="i-lucide-signal text-xs" />
            <i class="i-lucide-wifi text-xs" />
            <i class="i-lucide-battery text-xs" />
          </div>
        </div>
      </div>

      <!-- Chat header -->
      <div class="whatsapp-header">
        <div class="flex items-center gap-3">
          <i class="i-lucide-arrow-left" />
          <div
            class="w-9 h-9 bg-gray-400 rounded-full overflow-hidden flex items-center justify-center"
          >
            <i class="i-lucide-user text-white text-lg" />
          </div>
          <div class="flex-1">
            <div
              class="text-sm font-medium"
              v-dompurify-html="recipientNameHtml"
            ></div>
            <div class="text-xs opacity-70">online</div>
          </div>
          <div class="flex items-center gap-4">
            <i class="i-lucide-phone" />
            <i class="i-lucide-video" />
            <i class="i-lucide-more-vertical" />
          </div>
        </div>
      </div>

      <!-- Chat messages -->
      <div class="whatsapp-chat" :style="whatsappChatStyle">
        <div class="whatsapp-chat-container">
          <!-- Message bubble with media (WhatsApp style) -->
          <div class="flex justify-end mb-2">
            <div
              class="whatsapp-message-sent"
              :class="{ 'whatsapp-media-message': hasAttachments }"
            >
              <!-- Media content (displayed first like WhatsApp) -->
              <div
                v-if="attachments && attachments.length > 0"
                class="whatsapp-media-container"
              >
                <div
                  v-for="attachment in attachments"
                  :key="attachment.id"
                  class="whatsapp-media-item"
                >
                  <!-- Image preview -->
                  <template v-if="isImageFile(attachment.type)">
                    <div class="whatsapp-image-container">
                      <img
                        :src="attachment.url"
                        :alt="attachment.name"
                        class="whatsapp-image"
                      />
                    </div>
                  </template>

                  <!-- Video preview -->
                  <template v-else-if="isVideoFile(attachment.type)">
                    <div class="whatsapp-video-container">
                      <video
                        :src="attachment.url"
                        class="whatsapp-video"
                        controls
                      />
                      <div class="whatsapp-video-overlay">
                        <i class="i-lucide-play text-white text-3xl" />
                      </div>
                    </div>
                  </template>

                  <!-- Document/Audio preview -->
                  <template v-else>
                    <div class="whatsapp-document">
                      <div class="flex items-center gap-3">
                        <div
                          class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"
                        >
                          <i
                            v-if="isAudioFile(attachment.type)"
                            class="i-lucide-audio-lines text-white text-xl"
                          />
                          <i v-else class="i-lucide-file text-white text-xl" />
                        </div>
                        <div class="flex-grow text-white">
                          <div class="font-medium text-sm">
                            {{ attachment.name }}
                          </div>
                          <div class="text-xs opacity-80">
                            {{ formatFileSize(attachment.size) }}
                          </div>
                        </div>
                        <i class="i-lucide-download text-white/80" />
                      </div>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Text content with smart time positioning -->
              <div
                v-if="message && message.trim()"
                class="whatsapp-text-content"
                :class="{ 'whatsapp-caption': hasAttachments }"
              >
                <div class="whatsapp-text-with-time-smart">
                  <span
                    class="text-sm leading-relaxed whitespace-pre-wrap"
                    v-html="formatWhatsAppText(message)"
                  />
                  <span class="whatsapp-time-smart">
                    <span class="text-xs opacity-60">{{ currentTime }}</span>
                    <svg
                      viewBox="0 0 18 13"
                      height="11"
                      width="16"
                      class="whatsapp-check"
                    >
                      <path
                        d="M11.0714 0.652832C10.991 0.585124 10.8894 0.55127 10.7667 0.55127C10.6186 0.55127 10.4916 0.610514 10.3858 0.729004L4.19688 8.36523L1.79112 6.09277C1.7488 6.04622 1.69802 6.01025 1.63877 5.98486C1.57953 5.95947 1.51817 5.94678 1.45469 5.94678C1.32351 5.94678 1.20925 5.99544 1.11192 6.09277L0.800883 6.40381C0.707784 6.49268 0.661235 6.60482 0.661235 6.74023C0.661235 6.87565 0.707784 6.98991 0.800883 7.08301L3.79698 10.0791C3.94509 10.2145 4.11224 10.2822 4.29844 10.2822C4.40424 10.2822 4.5058 10.259 4.60313 10.2124C4.70046 10.1659 4.78086 10.1003 4.84434 10.0156L11.4903 1.59863C11.5623 1.5013 11.5982 1.40186 11.5982 1.30029C11.5982 1.14372 11.5348 1.01888 11.4078 0.925781L11.0714 0.652832ZM8.6212 8.32715C8.43077 8.20866 8.2488 8.09017 8.0753 7.97168C7.99489 7.89128 7.8891 7.85107 7.75791 7.85107C7.6098 7.85107 7.4892 7.90397 7.3961 8.00977L7.10411 8.33984C7.01947 8.43717 6.97715 8.54508 6.97715 8.66357C6.97715 8.79476 7.0237 8.90902 7.1168 9.00635L8.1959 10.0791C8.33132 10.2145 8.49636 10.2822 8.69102 10.2822C8.79681 10.2822 8.89838 10.259 8.99571 10.2124C9.09304 10.1659 9.17556 10.1003 9.24327 10.0156L15.8639 1.62402C15.9358 1.53939 15.9718 1.43994 15.9718 1.32568C15.9718 1.1818 15.9125 1.05697 15.794 0.951172L15.4386 0.678223C15.3582 0.610514 15.2587 0.57666 15.1402 0.57666C14.9964 0.57666 14.8715 0.635905 14.7657 0.754395L8.6212 8.32715Z"
                        fill="#4FC3F7"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              <!-- Placeholder when no message -->
              <div v-else-if="!hasAttachments" class="whatsapp-text-content">
                <div class="whatsapp-text-with-time-smart">
                  <span class="text-sm leading-relaxed whitespace-pre-wrap"
                    >Digite sua mensagem...</span
                  >
                  <span class="whatsapp-time-smart">
                    <span class="text-xs opacity-60">{{ currentTime }}</span>
                    <svg
                      viewBox="0 0 18 13"
                      height="11"
                      width="16"
                      class="whatsapp-check"
                    >
                      <path
                        d="M11.0714 0.652832C10.991 0.585124 10.8894 0.55127 10.7667 0.55127C10.6186 0.55127 10.4916 0.610514 10.3858 0.729004L4.19688 8.36523L1.79112 6.09277C1.7488 6.04622 1.69802 6.01025 1.63877 5.98486C1.57953 5.95947 1.51817 5.94678 1.45469 5.94678C1.32351 5.94678 1.20925 5.99544 1.11192 6.09277L0.800883 6.40381C0.707784 6.49268 0.661235 6.60482 0.661235 6.74023C0.661235 6.87565 0.707784 6.98991 0.800883 7.08301L3.79698 10.0791C3.94509 10.2145 4.11224 10.2822 4.29844 10.2822C4.40424 10.2822 4.5058 10.259 4.60313 10.2124C4.70046 10.1659 4.78086 10.1003 4.84434 10.0156L11.4903 1.59863C11.5623 1.5013 11.5982 1.40186 11.5982 1.30029C11.5982 1.14372 11.5348 1.01888 11.4078 0.925781L11.0714 0.652832ZM8.6212 8.32715C8.43077 8.20866 8.2488 8.09017 8.0753 7.97168C7.99489 7.89128 7.8891 7.85107 7.75791 7.85107C7.6098 7.85107 7.4892 7.90397 7.3961 8.00977L7.10411 8.33984C7.01947 8.43717 6.97715 8.54508 6.97715 8.66357C6.97715 8.79476 7.0237 8.90902 7.1168 9.00635L8.1959 10.0791C8.33132 10.2145 8.49636 10.2822 8.69102 10.2822C8.79681 10.2822 8.89838 10.259 8.99571 10.2124C9.09304 10.1659 9.17556 10.1003 9.24327 10.0156L15.8639 1.62402C15.9358 1.53939 15.9718 1.43994 15.9718 1.32568C15.9718 1.1818 15.9125 1.05697 15.794 0.951172L15.4386 0.678223C15.3582 0.610514 15.2587 0.57666 15.1402 0.57666C14.9964 0.57666 14.8715 0.635905 14.7657 0.754395L8.6212 8.32715Z"
                        fill="#4FC3F7"
                      />
                    </svg>
                  </span>
                </div>
              </div>

              <!-- Time overlay for media-only messages -->
              <div
                v-if="hasAttachments && (!message || !message.trim())"
                class="whatsapp-message-info whatsapp-overlay-info gap-2"
              >
                <span class="text-xs opacity-60">{{ currentTime }}</span>
                <svg
                  viewBox="0 0 18 13"
                  height="11"
                  width="16"
                  class="whatsapp-check"
                >
                  <path
                    d="M11.0714 0.652832C10.991 0.585124 10.8894 0.55127 10.7667 0.55127C10.6186 0.55127 10.4916 0.610514 10.3858 0.729004L4.19688 8.36523L1.79112 6.09277C1.7488 6.04622 1.69802 6.01025 1.63877 5.98486C1.57953 5.95947 1.51817 5.94678 1.45469 5.94678C1.32351 5.94678 1.20925 5.99544 1.11192 6.09277L0.800883 6.40381C0.707784 6.49268 0.661235 6.60482 0.661235 6.74023C0.661235 6.87565 0.707784 6.98991 0.800883 7.08301L3.79698 10.0791C3.94509 10.2145 4.11224 10.2822 4.29844 10.2822C4.40424 10.2822 4.5058 10.259 4.60313 10.2124C4.70046 10.1659 4.78086 10.1003 4.84434 10.0156L11.4903 1.59863C11.5623 1.5013 11.5982 1.40186 11.5982 1.30029C11.5982 1.14372 11.5348 1.01888 11.4078 0.925781L11.0714 0.652832ZM8.6212 8.32715C8.43077 8.20866 8.2488 8.09017 8.0753 7.97168C7.99489 7.89128 7.8891 7.85107 7.75791 7.85107C7.6098 7.85107 7.4892 7.90397 7.3961 8.00977L7.10411 8.33984C7.01947 8.43717 6.97715 8.54508 6.97715 8.66357C6.97715 8.79476 7.0237 8.90902 7.1168 9.00635L8.1959 10.0791C8.33132 10.2145 8.49636 10.2822 8.69102 10.2822C8.79681 10.2822 8.89838 10.259 8.99571 0.2124C9.09304 10.1659 9.17556 10.1003 9.24327 10.0156L15.8639 1.62402C15.9358 1.53939 15.9718 1.43994 15.9718 1.32568C15.9718 1.1818 15.9125 1.05697 15.794 0.951172L15.4386 0.678223C15.3582 0.610514 15.2587 0.57666 15.1402 0.57666C14.9964 0.57666 14.8715 0.635905 14.7657 0.754395L8.6212 8.32715Z"
                    fill="#4FC3F7"
                  />
                </svg>
              </div>

              <!-- WhatsApp Template Buttons - Inside the message bubble -->
              <div
                v-if="buttons && buttons.length > 0"
                class="whatsapp-buttons-section"
              >
                <div class="whatsapp-buttons-separator" />
                <div
                  v-for="(button, index) in buttons"
                  :key="index"
                  class="whatsapp-button"
                  :class="{
                    'whatsapp-button-reply': button.type === 'QUICK_REPLY',
                    'whatsapp-button-url': button.type === 'URL',
                    'whatsapp-button-phone': button.type === 'PHONE_NUMBER',
                  }"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    class="whatsapp-button-arrow"
                  >
                    <path
                      d="M9.72,7.43C17,8.47,20.5,13.3,21.93,18.48 c0.1,0.36-0.36,0.59-0.6,0.32c-2.77-3.16-6.51-4.61-11.6-4.61v2.73c0,0.72-0.87,1.08-1.38,0.57l-6.05-6.05 c-0.32-0.32-0.32-0.83,0-1.15l6.05-6.05c0.51-0.51,1.38-0.15,1.38,0.57V7.43z"
                      fill="currentColor"
                    />
                  </svg>
                  <span class="whatsapp-button-text">{{ button.text }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input area -->
      <div class="whatsapp-compose">
        <div class="flex items-center gap-3 px-3">
          <i class="i-lucide-smile text-gray-500 dark:text-gray-400" />
          <div
            class="flex-1 bg-white dark:bg-slate-700 rounded-full px-4 py-2 text-sm text-gray-500"
          >
            Mensagem
          </div>
          <i class="i-lucide-paperclip text-gray-500 dark:text-gray-400" />
          <i class="i-lucide-camera text-gray-500 dark:text-gray-400" />
          <div
            class="w-10 h-10 bg-[#008a7c] rounded-full flex items-center justify-center"
          >
            <i class="i-lucide-mic text-white" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* WhatsApp Preview Styles */
.whatsapp-container {
  background: #f0f0f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  justify-content: center;
}

.dark .whatsapp-container {
  background: #1a1a1a;
}

.whatsapp-device {
  width: 380px;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
}

.dark .whatsapp-device {
  background: #0b141a;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
}

/* Status Bar */
.whatsapp-status-bar {
  background: #075e54;
  color: white;
  padding: 6px 0;
  font-size: 12px;
}

.dark .whatsapp-status-bar {
  background: #1f2c33;
}

/* Chat Header */
.whatsapp-header {
  background: #075e54;
  color: white;
  padding: 10px 16px;
  font-size: 16px;
}

.dark .whatsapp-header {
  background: #2a3942;
}

/* Chat Area */
.whatsapp-chat {
  height: 400px;
  overflow: hidden;
  position: relative;
}

.whatsapp-chat-container {
  background-color: transparent;
  height: 100%;
  overflow-y: auto;
  padding: 12px 16px;
  position: relative;
  z-index: 1;
}

/* Message styles */
.whatsapp-message-sent {
  padding: 2px;
  background: #e1ffc7;
  color: #000;
  padding: 4px 6px;
  border-radius: 8px 8px 8px 8px;
  max-width: 75%;
  position: relative;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  overflow: hidden;
  /* Ensure the message bubble can expand properly */
  min-width: 0;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.dark .whatsapp-message-sent {
  padding: 3px;
  background: #015c53;
  color: #fff;
}

/* Media message styles */
.whatsapp-media-message {
  padding: 4px;
  overflow: hidden;
}

.whatsapp-media-container {
  margin: 0;
}

.whatsapp-media-item {
  position: relative;
}

/* Image styles */
.whatsapp-image-container {
  position: relative;
  max-width: 100%;
  border-radius: 8px 8px 8px 8px;
  overflow: hidden;
}

.whatsapp-image {
  width: 100%;
  max-width: 280px;
  height: auto;
  max-height: 320px;
  object-fit: cover;
  display: block;
}

/* Video styles */
.whatsapp-video-container {
  position: relative;
  max-width: 280px;
  border-radius: 8px 0 8px 8px;
  overflow: hidden;
}

.whatsapp-video {
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: cover;
  display: block;
}

.whatsapp-video-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* Document styles */
.whatsapp-document {
  background: rgba(0, 0, 0, 0.1);
  padding: 12px;
  min-width: 200px;
  max-width: 280px;
}

.dark .whatsapp-document {
  background: rgba(255, 255, 255, 0.1);
}

/* Text content styles - Updated for smart time positioning */
.whatsapp-text-content {
  padding: 4px 1px 8px 4px;
  min-width: 0;
  width: 100%;
}

.whatsapp-caption {
  padding: 8px 4px 5px 12px;
}

/* Smart text and time layout - only reserves space on last line */
.whatsapp-text-with-time-smart {
  position: relative;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  white-space: pre-wrap;
  min-height: 20px;
}

/* Reserve space for timestamp only on the last line */
.whatsapp-text-with-time-smart::after {
  content: '';
  display: inline-block;
  width: 55px;
  height: 0;
}

/* Time positioned smart - tries to stay on last line with horizontal gap */
.whatsapp-time-smart {
  position: absolute;
  bottom: -8px;
  right: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  background: inherit;
  padding-left: 8px; /* Horizontal gap from text */
  /* Subtle gradient to blend with background when overlapping */
  background: linear-gradient(to right, transparent 0%, currentColor 8px);
  background-clip: text;
  -webkit-background-clip: text;
}

/* Override background for time elements to ensure visibility */
.whatsapp-time-smart span,
.whatsapp-time-smart svg {
  background: inherit;
  position: relative;
  z-index: 1;
}

/* Light mode time background */
.whatsapp-message-sent .whatsapp-time-smart {
  background: linear-gradient(to right, transparent 0%, #e1ffc7 8px);
}

/* Dark mode time background */
.dark .whatsapp-message-sent .whatsapp-time-smart {
  background: linear-gradient(to right, transparent 0%, #015c53 8px);
}

/* Message info (time + checkmarks) */
.whatsapp-message-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 4px;
  padding: 0 8px 4px 0;
}

.whatsapp-overlay-info {
  position: absolute;
  bottom: 8px;
  right: 0px;
  padding: 0px 8px;
  border-radius: 12px;
  color: white;
}

.whatsapp-overlay-info .text-xs {
  color: white;
  opacity: 1;
}

.whatsapp-message-sent::after {
  content: '';
  position: absolute;
  top: 0;
  right: -8px;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 0 10px 10px;
  border-color: transparent transparent transparent #e1ffc7;
}

.dark .whatsapp-message-sent::after {
  border-color: transparent transparent transparent #015c53;
}

/* Check mark styles */
.whatsapp-check {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* Compose area */
.whatsapp-compose {
  background: #f0f0f0;
  padding: 10px 8px;
  border-top: 1px solid #e0e0e0;
}

.dark .whatsapp-compose {
  background: #1e2428;
  border-top: 1px solid #2a3942;
}

/* WhatsApp text formatting styles - Use deep selector for v-html content */
.whatsapp-text-content :deep(.whatsapp-footer) {
  font-size: 0.9em;
  font-weight: 300;
  color: #000000;
  opacity: 0.7;
  margin-top: 4px;
}

.dark .whatsapp-text-content :deep(.whatsapp-footer) {
  color: #ffffff;
  opacity: 0.6;
}

/* WhatsApp Buttons Styling - Part of the message bubble */
.whatsapp-buttons-section {
  margin-top: 8px;
  padding-top: 0;
}

.whatsapp-buttons-separator {
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 0 -6px 4px -6px;
}

.dark .whatsapp-buttons-separator {
  background: rgba(255, 255, 255, 0.1);
}

.whatsapp-button {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 0;
  padding: 8px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: #000000;
  text-align: center;
  min-height: 36px;
  margin: 0 -6px;
}

.whatsapp-button:last-child {
  border-bottom: none;
}

.whatsapp-button:hover {
  background: rgba(0, 0, 0, 0.04);
}

.whatsapp-button:active {
  background: rgba(0, 0, 0, 0.08);
}

.dark .whatsapp-button {
  color: #ffffff;
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.dark .whatsapp-button:hover {
  background: rgba(255, 255, 255, 0.04);
}

.dark .whatsapp-button:active {
  background: rgba(255, 255, 255, 0.08);
}

.whatsapp-button-arrow {
  flex-shrink: 0;
  opacity: 0.7;
}

.whatsapp-button-text {
  text-align: center;
}

/* Hide the old icon spans since we're using the arrow SVG */
.whatsapp-button-icon {
  display: none;
}

/* Scrollbar styles */
.whatsapp-chat-container::-webkit-scrollbar {
  width: 6px;
}

.whatsapp-chat-container::-webkit-scrollbar-track {
  background: transparent;
}

.whatsapp-chat-container::-webkit-scrollbar-thumb {
  background: #b3ada7;
  border-radius: 3px;
}

.dark .whatsapp-chat-container::-webkit-scrollbar-thumb {
  background: #4a5568;
}
</style>
